"use client"

import { Fragment, useState } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { HomeIcon, MenuIcon, XIcon, LogoutIcon, UserIcon, ChartBarIcon, DocumentTextIcon } from "@heroicons/react/outline"
import { Link } from "react-router-dom"

const navigation = [
  { name: "My Tests", href: "/dashboard", icon: HomeIcon, currentName: "dashboard" },
  { name: "Profile", href: "/dashboard/profile", icon: UserIcon, currentName: "profile" },
  { name: "Analytics", href: "/dashboard/analytics", icon: ChartBarIcon, currentName: "analytics" },
  { name: "Terms", href: "/dashboard/terms", icon: DocumentTextIcon, currentName: "terms" },
]

function classNames(...classes) {
  return classes.filter(Boolean).join(" ")
}

export default function Dashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [currentComponent, setCurrrentComponent] = useState("dashboard")

  return (
    <div>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 flex z-40 md:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-400 bg-opacity-40 backdrop-blur-sm" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-gradient-to-b from-[#F7F5FF] to-[#F0EDFF] border-r border-gray-200 shadow-xl">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#7035de]"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex-shrink-0 flex justify-center items-center px-4 mb-8">
                  <a href="https://typescience.ai/" className="flex items-center">
                    <div className="relative">
                      <img
                        className="h-14 w-14 rounded-full border-2 border-white shadow-md object-cover"
                        alt="TS"
                        src="/TS-Logov3.png"
                      />
                      <div className="absolute -bottom-1 -right-1 bg-[#7035de] rounded-full p-1 shadow-md">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <span className="ml-3 text-lg font-bold text-gray-800">TypeScience</span>
                  </a>
                </div>
                <nav className="mt-5 px-3 space-y-2">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setCurrrentComponent(item.currentName)}
                      className={classNames(
                        item.currentName === currentComponent
                          ? "bg-white text-[#7035de] shadow-md border-l-4 border-[#7035de]"
                          : "text-gray-600 hover:bg-white/60 hover:text-[#7035de] border-l-4 border-transparent",
                        "group flex items-center px-4 py-3 text-base font-medium rounded-r-xl transition-all duration-200"
                      )}
                    >
                      <item.icon
                        className={classNames(
                          item.currentName === currentComponent
                            ? "text-[#7035de]"
                            : "text-gray-500 group-hover:text-[#7035de]",
                          "mr-4 flex-shrink-0 h-6 w-6 transition-colors duration-200"
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                  ))}
                </nav>
              </div>
              <div className="px-3 pb-4">
                <div className="bg-white/60 rounded-xl p-3 shadow-sm border border-gray-100">
                  <div className="flex items-center mb-3">
                    <div className="h-9 w-9 rounded-full bg-[#F0EDFF] flex items-center justify-center text-[#7035de]">
                      <UserIcon className="h-5 w-5" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-800">User Account</p>
                      <p className="text-xs text-gray-500">Free Plan</p>
                    </div>
                  </div>
                  <a
                    href={"https://typescience.ai/"}
                    className="flex items-center justify-center w-full py-2 px-3 text-sm font-medium rounded-lg text-white bg-[#7035de] hover:bg-[#5a28b5] transition-colors duration-200"
                  >
                    <LogoutIcon className="h-4 w-4 mr-2" aria-hidden="true" />
                    Sign Out
                  </a>
                </div>
              </div>
            </div>
          </Transition.Child>
          <div className="flex-shrink-0 w-14">{/* Force sidebar to shrink to fit close icon */}</div>
        </Dialog>
      </Transition.Root>
      
      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:w-28 md:flex-col md:fixed md:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 bg-gradient-to-b from-[#F7F5FF] to-[#F0EDFF] border-r border-gray-200 rounded-tr-xl rounded-br-xl shadow-lg m-2 my-3">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center justify-center flex-shrink-0 px-4 mb-8">
              <a href="https://typescience.ai/" className="relative group">
                <div className="relative">
                  <img
                    className="h-14 w-14 rounded-full border-2 border-white shadow-md object-cover transition-transform duration-300 group-hover:scale-105"
                    alt="TS"
                    src="/TS-Logov3.png"
                  />
                  <div className="absolute -bottom-1 -right-1 bg-[#7035de] rounded-full p-1 shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <span className="sr-only">TypeScience</span>
              </a>
            </div>
            
            <nav className="mt-2 flex-1 px-2 space-y-3">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setCurrrentComponent(item.currentName)}
                  className={classNames(
                    item.currentName === currentComponent
                      ? "bg-white text-[#7035de] shadow-md border-l-4 border-[#7035de]"
                      : "text-gray-600 hover:bg-white/60 hover:text-[#7035de] border-l-4 border-transparent",
                    "group flex flex-col items-center px-2 py-3 text-sm font-medium rounded-r-xl transition-all duration-200"
                  )}
                >
                  <item.icon
                    className={classNames(
                      item.currentName === currentComponent
                        ? "text-[#7035de]"
                        : "text-gray-500 group-hover:text-[#7035de]",
                      "h-6 w-6 mb-1 transition-colors duration-200"
                    )}
                    aria-hidden="true"
                  />
                  <span className="text-xs">{item.name}</span>
                </Link>
              ))}
            </nav>
          </div>
          
          <div className="p-2 mb-2">
            <a
              href={"https://typescience.ai/"}
              className="flex flex-col items-center justify-center p-3 rounded-xl text-gray-600 hover:bg-white/60 hover:text-[#7035de] transition-colors duration-200"
            >
              <LogoutIcon className="h-6 w-6 mb-1 text-[#7035de]" aria-hidden="true" />
              <span className="text-xs text-[#7035de]">Sign Out</span>
            </a>
          </div>
        </div>
      </div>
      
      <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-[#F7F5FF]">
        <button
          type="button"
          className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-[#7035de] hover:text-[#5a28b5] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#7035de]"
          onClick={() => setSidebarOpen(true)}
        >
          <span className="sr-only">Open sidebar</span>
          <MenuIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
